import { useRef } from 'react';
import { API } from '../../utils/amplifyWrapper';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { uploadToS3 } from './useClonedVoice';
import { assertFeatureCreditsOk } from '../organisation/featureCreditsGuard';

export interface IVoiceChange {
    id: string;
    integration_id: string;
    name: string;
    source_audio_path: string;
    source_format?: string;
    audio_duration?: number;
    target_voice_code?: string;
    target_voice_name?: string;
    target_voice_audio: string;
    lang_code?: string;
    settings?: Record<string, number | boolean>;
    status: 'processing' | 'ready' | 'failed';
    vocals_path?: string;
    converted_path?: string;
    output_format?: string;
    output_path?: string;
    job_id?: string;
    error?: string;
    created_at?: string;
    updated_at?: string;
}

// ── List ─────────────────────────────────────────────────────────────────────
export const fetchVoiceChanges = async (org_id: string, integration_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_change`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as IVoiceChange[];
};

export const useVoiceChanges = ({ org_id, integration_id }: { org_id: string; integration_id: string }) => {
    const queryClient = useQueryClient();
    // Tracks how many jobs were still in flight on the previous poll, so we can detect the
    // moment one settles — that's when credits were deducted (charge-on-completion), so the
    // balance needs refreshing.
    const prevActiveRef = useRef<number | null>(null);
    return useQuery(
        ['voice_changes', org_id, integration_id],
        () => fetchVoiceChanges(org_id, integration_id),
        {
            enabled: !!(org_id && integration_id),
            // Poll while any job is still processing so the history flips to ready/failed on its own.
            refetchInterval: (data) =>
                data && data.some((c) => c.status === 'processing') ? 5000 : false,
            onSuccess: (data) => {
                const active = data.filter((c) => c.status === 'processing').length;
                if (prevActiveRef.current !== null && active < prevActiveRef.current) {
                    queryClient.invalidateQueries(['organisation_usage', org_id]);
                }
                prevActiveRef.current = active;
            },
        }
    );
};

export const fetchVoiceChangeById = async (org_id: string, integration_id: string, change_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_change/${change_id}`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as IVoiceChange;
};

// ── Presigned upload (audio-only, lands in the cloned-voice/preview bucket) ─────
export const fetchChangerPresignedUrl = async ({
    org_id,
    integration_id,
    fileName,
    contentType,
}: {
    org_id: string;
    integration_id: string;
    fileName: string;
    contentType: string;
}) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/audio/signed-url?file_name=${encodeURIComponent(fileName)}&content_type=${encodeURIComponent(contentType)}&file_name_prefix=voice-changer&file_type=voice-changer`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as { signed_url: string; url: string };
};

// ── Create ───────────────────────────────────────────────────────────────────
interface ICreateVoiceChangeParams {
    org_id: string;
    integration_id: string;
    name: string;
    source_audio_path: string;
    source_format: string;
    audio_duration?: number;
    target_voice_audio: string;
    target_voice_code?: string;
    target_voice_name?: string;
    lang_code?: string;
    settings?: Record<string, number | boolean>;
}

export const useCreateVoiceChange = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({
            org_id,
            integration_id,
            name,
            source_audio_path,
            source_format,
            audio_duration,
            target_voice_audio,
            target_voice_code,
            target_voice_name,
            lang_code,
            settings,
        }: ICreateVoiceChangeParams) => {
            const response = await API.post(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_change`,
                {
                    body: {
                        name,
                        source_audio_path,
                        source_format,
                        audio_duration,
                        target_voice_audio,
                        target_voice_code,
                        target_voice_name,
                        lang_code,
                        settings,
                    },
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            assertFeatureCreditsOk(response);
            return { changeId: response.data.id as string, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_changes', org_id, integration_id]);
            },
        }
    );
};

// ── Update / Delete ────────────────────────────────────────────────────────────
export const useUpdateVoiceChange = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({
            org_id,
            integration_id,
            change_id,
            name,
            status,
        }: {
            org_id: string;
            integration_id: string;
            change_id: string;
            name?: string;
            status?: 'processing' | 'ready' | 'failed';
        }) => {
            const response = await API.put(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_change/${change_id}`,
                { body: { name, status }, headers: { 'Content-Type': 'application/json' } }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_changes', org_id, integration_id]);
            },
        }
    );
};

export const useDeleteVoiceChange = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ org_id, integration_id, change_id }: { org_id: string; integration_id: string; change_id: string }) => {
            const response = await API.del(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_change/${change_id}`,
                { headers: { 'Content-Type': 'application/json' } }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_changes', org_id, integration_id]);
            },
        }
    );
};

// ── Download the finished output → signed URL ──────────────────────────────────
export const fetchVoiceChangeDownloadUrl = async (org_id: string, integration_id: string, change_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_change/${change_id}/download`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as { url: string };
};

export { uploadToS3 };
